/**
 * ActionSheet Component
 * 
 * A responsive component that renders as:
 * - Mobile (<768px): Bottom drawer using Vaul (thumb-friendly, swipe-to-dismiss)
 *   - Falls back to BasicDialog if Vaul is unavailable
 * - Desktop: BasicDialog with list layout
 * 
 * Follows Material 3 decision UI patterns.
 */

import { ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { BasicDialog } from "./BasicDialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";

// Runtime check for Vaul availability (matches drawer.tsx logic)
let isVaulAvailable = true;
try {
  // This will be false if Vaul primitives failed to load
  const { Drawer: VaulDrawer } = require("vaul");
  isVaulAvailable = Boolean(VaulDrawer?.Root);
} catch {
  isVaulAvailable = false;
  // Using warn instead of error - this is expected fallback behavior, not an error
  console.warn("[ActionSheet] Vaul not available - using BasicDialog fallback");
}

export interface ActionSheetProps {
  /** Controls open/close state */
  open: boolean;
  /** Callback when state changes */
  onOpenChange: (open: boolean) => void;
  /** Sheet title */
  title: string;
  /** Optional description */
  description?: string;
  /** Icon to display in title */
  icon?: ReactNode;
  /** Tone affects styling (default, warning, destructive) */
  tone?: "default" | "warning" | "destructive";
  /** Main content (reason list, etc.) */
  children: ReactNode;
  /** Footer content (action buttons) */
  footer?: ReactNode;
}

export function ActionSheet({
  open,
  onOpenChange,
  title,
  description,
  icon,
  tone = "default",
  children,
  footer,
}: ActionSheetProps) {
  const isMobile = useIsMobile();

  // Mobile: Bottom drawer with Vaul (if available)
  if (isMobile && isVaulAvailable) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle
              className={`flex items-center gap-2 ${
                tone === "destructive"
                  ? "text-destructive"
                  : tone === "warning"
                  ? "text-orange-600"
                  : ""
              }`}
            >
              {icon}
              {title}
            </DrawerTitle>
            {description && (
              <DrawerDescription>{description}</DrawerDescription>
            )}
          </DrawerHeader>
          
          <div className="px-4 pb-4 overflow-y-auto">
            {children}
          </div>
          
          {footer && (
            <DrawerFooter className="pt-2">
              {footer}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop OR Mobile fallback (when Vaul unavailable): BasicDialog
  return (
    <BasicDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      icon={icon}
      tone={tone}
      closeOnOutsideClick={tone !== "destructive"}
    >
      <div className="space-y-4">
        {children}
        {footer && (
          <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end sm:gap-2">
            {footer}
          </div>
        )}
      </div>
    </BasicDialog>
  );
}
